import React, { useMemo } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap"
import { MetaTags } from "react-meta-tags"
import { AppConfiguration } from "config"
import { useDispatch, useSelector } from "react-redux"
import {
  exportApiTimingsData,
  getAggrTagsData,
  getApiTimingsData,
  getProfilingTags,
} from "store/admin/dev-dashboard/actions"
import { useCallback, useRef, useState } from "react"
import ReactMultiselectCheckboxes from "react-multiselect-checkboxes/lib/ReactMultiselectCheckboxes"
import { useEffect } from "react"
import CustomDateRangePicker, {
  createOption,
  TIME_RANGE_OPTIONS,
} from "components/Common/DateRangePicker"
import {
  getAppropriateLoadingGif,
  getIANATimezoneStringForAbbreviation,
  getPrimaryButtonClass,
  SfCommaStringFormatter,
} from "helpers/sf_utils"
import {
  API_STATUS_OPTIONS,
  ELAPSED_TIME_TYPE_OPTIONS,
  REPORTING_TIMEZONE,
  REQUIRED_FEATURE_FLAGS,
  SfFilterRelationalOperators,
} from "helpers/sf_constants"
import useFeatureFlags from "hooks/useFeatureFlags"
import Select from "react-select"
import NumberFormat from "react-number-format"
import StatsCard from "./StatsCard"
import DevDashboardTable from "./DevDashboardTable"
import {
  bsh_getAdminUserDetails,
  bsh_getInsuranceIoUserDetails,
} from "helpers/sf_browser_storage_helper"

const DEFAULT_PAGE_SIZE = 30
const UNTAGGED_API_VALUE = "untagged_api"
const DEFAULT_TAG_OPTIONS = [
  { label: "All", value: 0 },
  { label: "Untagged Api", value: UNTAGGED_API_VALUE },
]
const VALUE_FOR_ALL = 0

const DevDashboard = () => {
  const [isFeatureFlagActive] = useFeatureFlags()
  const shouldUseOffsetPagination = useMemo(() => (
    isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_OFFSET_PAGINATION_APPLICATION)
  ), [isFeatureFlagActive])

  const {
    current_user,
    profiling_tags,
    profiling_tags_loading,
    aggr_tags_data,
    aggr_tags_data_loading,
    api_timings_export_loading,
  } = useSelector(state => ({
    current_user: state.LoginAdminUser.current_user,
    profiling_tags: state.DevDashboard.profiling_tags,
    aggr_tags_data: state.DevDashboard.aggr_tags_data,
    profiling_tags_loading: state.DevDashboard.profiling_tags_loading,
    aggr_tags_data_loading: state.DevDashboard.aggr_tags_data_loading,
    api_timings_export_loading: state.DevDashboard.api_timings_export_loading,
  }))

  const dispatch = useDispatch()
  const insurance_io_user_token = bsh_getInsuranceIoUserDetails()?.token
  const admin_user_token = bsh_getAdminUserDetails()?.token

  useEffect(() => {
    dispatch(getProfilingTags())
    applyFilters([], false)
    applyFilters([], true)
    applyFiltersOnApiTimingsData([])
    // applyFilters([], true, getAggrTagsData)
  }, [])

  const getProfilingTagsOptions = profiling_tags => {
    return profiling_tags.reduce((a, c) => {
      return [
        ...a,
        {
          label: c?.name,
          value: c?.id,
        },
      ]
    }, DEFAULT_TAG_OPTIONS)
  }

  const getDropdownLabelForProfilingTags = ({
    placeholderButtonLabel,
    value,
  }) => {
    if (value && value?.some(o => o.value === VALUE_FOR_ALL)) {
      return (
        <span className="css-1v99tuv text-black align-self-center">All</span>
      )
    }
    return (
      <span className="css-1v99tuv text-black align-self-center">
        {value?.length} selected
      </span>
    )
  }

  const getDropdownLabelForApiResponseCode = ({
    placeholderButtonLabel,
    value,
  }) => {
    if (value && value?.some(o => o.value === VALUE_FOR_ALL)) {
      return (
        <span className="css-1v99tuv text-black align-self-center">All</span>
      )
    }
    return (
      <span className="css-1v99tuv text-black align-self-center">
        {value?.length} selected
      </span>
    )
  }

  const handleSelectTags = (selectedOption, event) => {
    const options = getProfilingTagsOptions(profiling_tags)
    if (event.action === "select-option" && event.option.value === VALUE_FOR_ALL) {
      setSelectedTags(options)
    } else if (event.action === "deselect-option" && event.option.value === VALUE_FOR_ALL) {
      setSelectedTags([])
    } else if (event.action === "deselect-option") {
      setSelectedTags(selectedOption.filter(o => o.value !== 0))
    } else if (selectedOption.length === options.length - 1) {
      setSelectedTags(options)
    } else {
      setSelectedTags(selectedOption)
    }
  }

  const handleSelectApiResponse = (selectedOption, event) => {
    const options = API_STATUS_OPTIONS
    if (event.action === "select-option" && event.option.value === VALUE_FOR_ALL) {
      setSelectedApiStatus(options)
    } else if (event.action === "deselect-option" && event.option.value === VALUE_FOR_ALL) {
      setSelectedApiStatus([])
    } else if (event.action === "deselect-option") {
      setSelectedApiStatus(selectedOption.filter(o => o.value !== VALUE_FOR_ALL))
    } else if (selectedOption.length === options.length - 1) {
      setSelectedApiStatus(options)
    } else {
      setSelectedApiStatus(selectedOption)
    }
  }

  //states
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedDateRange, setSelectedDateRange] = useState(
    createOption(
      TIME_RANGE_OPTIONS.LAST_HOUR,
      new Date(),
      getIANATimezoneStringForAbbreviation(
        current_user?.timezone || REPORTING_TIMEZONE
      )
    )
  )
  const [selectedElapsedTimeType, setSelectedElapsedTimeType] = useState(
    ELAPSED_TIME_TYPE_OPTIONS[0]
  )
  const [selectedApiStatus, setSelectedApiStatus] = useState(API_STATUS_OPTIONS)
  const [selectedElapsedTimeValue, setSelectedElapsedTimeValue] = useState(0)
  const [selectedElapsedTimeEndValue, setSelectedElapsedTimeEndValue] =
    useState(0)
  const currentApiTimingSortRef = useRef({
    sort_column: 'created_at',
    sort_direction: 'desc',
  })

  useEffect(() => {
    setSelectedTags(getProfilingTagsOptions(profiling_tags))
  }, [profiling_tags])

  let aggr_tags_data_for_card = useMemo(() => {
    return aggr_tags_data?.[0]
  },[aggr_tags_data])

  const preparePayload = selectedTags => {
    let selectedApiStatusValues = selectedApiStatus.map(o => o.value)
    const data = {
      start_date: selectedDateRange?.value?.startDate,
      end_date: selectedDateRange?.value?.endDate,
      elapsed_time_state_value: selectedElapsedTimeType?.value,
      elapsed_time_value: parseInt(selectedElapsedTimeValue || 0) * 1000,
      is_master:
        insurance_io_user_token && admin_user_token !== insurance_io_user_token
          ? 1
          : 0,
      api_response_codes: selectedApiStatusValues,
    }
    if (
      selectedApiStatusValues.includes(VALUE_FOR_ALL) ||
      selectedApiStatusValues.length === 0
    ) {
      data.api_response_codes = null
    }
    const selectedTagsValues = selectedTags.map(tag => tag.value)
    if (selectedTagsValues.includes(VALUE_FOR_ALL) || selectedTagsValues.length === 0) {
      data.profiling_tags = null
      data.untagged_apis = true
    } else {
      if (selectedTagsValues.includes(UNTAGGED_API_VALUE)) {
        data.untagged_apis = true
        data.profiling_tags = selectedTagsValues.filter(
          o => o != UNTAGGED_API_VALUE
        )
      } else {
        data.profiling_tags = selectedTagsValues
      }
    }
    if (
      selectedElapsedTimeType?.value === SfFilterRelationalOperators.BETWEEN
    ) {
      data.elapsed_time_end_value =
        parseInt(selectedElapsedTimeEndValue || 0) * 1000
    }
    return data
  }

  const applyFilters = (selectedTags, isGroupByTag) => {
    const data = preparePayload(selectedTags)
    if (isGroupByTag) {
      data.group_by_tag = "1"
    }
    dispatch(getAggrTagsData(data))
  }

  const buildApiTimingQueryParams = useCallback((selectedTags, pageNumber = 1, options = {}) => {
    const queryParams = preparePayload(selectedTags)
    queryParams.page_number = pageNumber
    queryParams.page_size = DEFAULT_PAGE_SIZE
    const sortColumn = options.sort_column || currentApiTimingSortRef.current.sort_column
    const sortDirection = options.sort_direction || currentApiTimingSortRef.current.sort_direction
    if (sortColumn) {
      queryParams.sort_column = sortColumn
      queryParams.sort_direction = sortDirection || 'desc'
    }
    return queryParams
  }, [preparePayload])

  const applyFiltersOnApiTimingsData = (
    selectedTags,
    last_page_fetched = 0,
    options = {}
  ) => {
    const pageNumber = Number.isFinite(Number(options.page_number))
      ? Number(options.page_number)
      : (last_page_fetched + 1)
    const queryParams = buildApiTimingQueryParams(selectedTags, pageNumber, options)
    dispatch(getApiTimingsData({
      queryParams,
      appendResults: typeof options.appendResults === 'boolean'
        ? options.appendResults
        : (!shouldUseOffsetPagination && last_page_fetched > 0),
      useCache: shouldUseOffsetPagination,
    }))
  }

  const onApiTimingSortChange = useCallback((sort_column, sort_direction) => {
    currentApiTimingSortRef.current = { sort_column, sort_direction }
    applyFiltersOnApiTimingsData(selectedTags, 0, {
      page_number: 1,
      appendResults: false,
      sort_column,
      sort_direction,
    })
  }, [applyFiltersOnApiTimingsData, selectedTags])

  const onExportApiTimingsData = useCallback(() => {
    const queryParams = buildApiTimingQueryParams(selectedTags, 1)
    delete queryParams.page_number
    delete queryParams.page_size
    dispatch(exportApiTimingsData(queryParams))
  }, [buildApiTimingQueryParams, dispatch, selectedTags])

  return (
    <div className="page-content" id="page-content-wrapper">
      <MetaTags>
        <title>Dev Monitor | {AppConfiguration.WEBSITE}</title>
      </MetaTags>
      {profiling_tags_loading ? (
        getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)
      ) : (
        <Container fluid>
          <Breadcrumbs title="" breadcrumbItem="Dev Monitor" />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <Row className="d-flex flex-row">
                    <Col className="align-self-end col-2">
                      <FormGroup className="mb-0">
                        <Label className="font-weight-semibold">
                          Profiling Tags
                        </Label>
                        <div>
                          <ReactMultiselectCheckboxes
                            hideSearch
                            getDropdownButtonLabel={
                              getDropdownLabelForProfilingTags
                            }
                            isMulti
                            value={selectedTags}
                            onChange={(selectedOption, event) => {
                              handleSelectTags(selectedOption, event)
                            }}
                            options={getProfilingTagsOptions(profiling_tags)}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <FormGroup className="mb-0">
                        <Label className="font-weight-semibold">
                          API Response
                        </Label>
                        <ReactMultiselectCheckboxes
                          hideSearch
                          getDropdownButtonLabel={
                            getDropdownLabelForApiResponseCode
                          }
                          isMulti
                          options={API_STATUS_OPTIONS}
                          onChange={(selectedOption, event) => {
                            handleSelectApiResponse(selectedOption, event)
                          }}
                          value={selectedApiStatus}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <FormGroup className="mb-0">
                        <Label className="font-weight-semibold">Time</Label>
                        <div>
                          <CustomDateRangePicker
                            ianaTz={getIANATimezoneStringForAbbreviation(
                              current_user?.timezone || REPORTING_TIMEZONE
                            )}
                            value={selectedDateRange}
                            onChange={setSelectedDateRange}
                            options={Object.keys(TIME_RANGE_OPTIONS).map(opt =>
                              createOption(
                                TIME_RANGE_OPTIONS[opt],
                                new Date(),
                                getIANATimezoneStringForAbbreviation(
                                  current_user?.timezone || REPORTING_TIMEZONE
                                )
                              )
                            )}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <FormGroup className="mb-0">
                        <Label className="font-weight-semibold">
                          Elapsed Time
                        </Label>
                        <Select
                          name="elapsed_time_type"
                          options={ELAPSED_TIME_TYPE_OPTIONS}
                          onChange={setSelectedElapsedTimeType}
                          value={selectedElapsedTimeType}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" className="align-self-end">
                      <Label
                        for="request_period"
                        className="form-label font-weight-semibold d-flex align-items-center"
                      >
                        Request Period (in sec)
                        <i
                          id="request_period-tooltip"
                          className="ms-1 bx bx-info-circle text-black bx-xs"
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target="request_period-tooltip"
                        >
                          Time in seconds for which the request limit applies
                        </UncontrolledTooltip>
                      </Label>
                      <FormGroup className="row mb-0">
                        <Col md={selectedElapsedTimeType?.value === SfFilterRelationalOperators.BETWEEN ? '5' : '12' }>
                          <NumberFormat
                            className={`form-control ${
                              selectedElapsedTimeType?.value ===
                              SfFilterRelationalOperators.BETWEEN
                                ? "w-40"
                                : ""
                            }`}
                            type="text"
                            name="elapsed_time_value"
                            onChange={e =>
                              setSelectedElapsedTimeValue(e.target.value)
                            }
                            value={selectedElapsedTimeValue}
                          />
                        </Col>
                        {selectedElapsedTimeType?.value === SfFilterRelationalOperators.BETWEEN && (
                          <Col md="7">
                            <div className="d-flex">
                              <div className="align-self-center font-weight-semibold me-3">To</div>
                                <NumberFormat
                                  className={`form-control w-40`}
                                  type="text"
                                  name="elapsed_time_end_value"
                                  onChange={e =>
                                    setSelectedElapsedTimeEndValue(e.target.value)
                                  }
                                  value={selectedElapsedTimeEndValue}
                                />
                            </div>
                          </Col>
                        )}
                      </FormGroup>
                    </Col>
                    <Col className="align-self-end">
                      <div>
                        <FormGroup className="mb-0">
                          <Button
                            className={`btn ${getPrimaryButtonClass()}`}
                            onClick={() => {
                              applyFilters(selectedTags, false, getAggrTagsData)
                              applyFilters(selectedTags, true, getAggrTagsData)
                              applyFiltersOnApiTimingsData(selectedTags)
                            }}
                          >
                            {/* TODO add loading */}
                            {aggr_tags_data_loading
                              ? "Loading Data..."
                              : "Update"}
                          </Button>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <StatsCard
                label="Total Requests"
                aggr_data={SfCommaStringFormatter(
                  aggr_tags_data_for_card?.total_requests
                )}
                loading={aggr_tags_data_loading}
              />
            </Col>
            <Col md={4}>
              <StatsCard
                label="Total Timeouts"
                aggr_data={SfCommaStringFormatter(
                  aggr_tags_data_for_card?.total_timeouts
                )}
                loading={aggr_tags_data_loading}
              />
            </Col>
            <Col md={4}>
              <StatsCard
                label="Total Errors"
                aggr_data={SfCommaStringFormatter(
                  aggr_tags_data_for_card?.total_errors
                )}
                loading={aggr_tags_data_loading}
              />
            </Col>
          </Row>
          <DevDashboardTable
            selectedTags={selectedTags}
            applyFiltersOnApiTimingsData={applyFiltersOnApiTimingsData}
            onApiTimingSortChange={onApiTimingSortChange}
            onExportApiTimingsData={onExportApiTimingsData}
            apiTimingsExportLoading={api_timings_export_loading}
            shouldUseOffsetPagination={shouldUseOffsetPagination}
          />
        </Container>
      )}
    </div>
  )
}

export default DevDashboard
